/**
 * Per-organization context: settings, enabled modules and the caller's own
 * permissions in the active organization.
 *
 * Why permissions live here and not in the token: they are authorization state
 * that can change at any moment, so the server resolves them per request. The
 * copy held here is for the UI ONLY — hiding a button the user cannot use. It
 * is never the thing that enforces anything; every action is re-checked
 * server-side, which is why `can()` being wrong is a cosmetic bug rather than
 * a security hole.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { iamApi, platformApi, hrApi } from '../services/index.js';
import { useAuth } from './AuthContext.jsx';

const TenantContext = createContext(null);

const DEFAULT_SETTINGS = {
  currency: 'INR',
  timezone: 'Asia/Kolkata',
  locale: 'en-IN',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  fiscalYearStartMonth: 4,
  firstDayOfWeek: 1,
  defaultLanguage: 'en',
  bilingualMode: false,
  taxEnabled: true,
  taxSystem: 'GST',
  taxLabel: 'GST',
  defaultTaxRate: '0',
  taxComponents: [],
};

/**
 * The organization's localization, remembered per organization so the next
 * visit renders amounts and dates correctly on the FIRST paint instead of
 * waiting a round trip. Refreshed from the server on every load; a cached copy
 * is only ever a head start, never the answer.
 */
const cacheKey = (tenantId) => `nexerp.localization.${tenantId}`;

function readCachedLocalization(tenantId) {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(cacheKey(tenantId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cacheLocalization(tenantId, value) {
  try {
    localStorage.setItem(cacheKey(tenantId), JSON.stringify(value));
  } catch {
    // Storage full or disabled: the next visit simply waits for the server.
  }
}

/**
 * Every member may read the localization subset. Against a backend that
 * predates that endpoint, fall back to the full settings (which need
 * `settings.read`), as this provider always did.
 */
const readLocalization = (tenantId) =>
  platformApi
    .getLocalization(tenantId)
    .catch((error) => (error?.status === 404 ? platformApi.getSettings(tenantId) : Promise.reject(error)));

export function TenantProvider({ children }) {
  const { tenantId, activeOrganization, user } = useAuth();

  const [settings, setSettings] = useState(() => {
    const cached = readCachedLocalization(tenantId);
    return cached ? { ...DEFAULT_SETTINGS, ...cached } : DEFAULT_SETTINGS;
  });
  // True once the organization's real localization is known — from the server,
  // or from the copy remembered last visit. Until then screens would format
  // with defaults, so the shell holds the page back (LocaleContext).
  const [localizationKnown, setLocalizationKnown] = useState(() => Boolean(readCachedLocalization(tenantId)));
  const [modules, setModules] = useState([]);
  // False until the modules call has answered: "no module list" must not read
  // as "every module is off" (the menu hides switched-off modules).
  const [modulesKnown, setModulesKnown] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState(new Set());
  const [isManager, setIsManager] = useState(false);
  // Starts true when there is an organization to load, so a guarded screen sees
  // "still loading" rather than an empty permission set on the first render.
  const [loading, setLoading] = useState(Boolean(tenantId));

  // The PRIMITIVES this loader actually reads. Depending on the objects instead
  // would re-issue all three calls whenever any unrelated part of the user or
  // the organization object was replaced — updating your own display name, for
  // instance, would silently refetch settings, modules and roles.
  //
  // The role set is flattened to a STRING for the same reason. A member holds
  // several roles, and `roleIds` is a fresh array on every response, so
  // depending on it directly would make this loader re-run on every render of
  // a parent — an array is never `===` a previous array with the same
  // contents. `roleId` is the fallback for a tenancy that has not been
  // redeployed yet.
  const membership = activeOrganization?.membership;
  const roleIdKey = (membership?.roleIds ?? [membership?.roleId]).filter(Boolean).join(',');
  const isOwner = Boolean(membership?.isOwner);
  const isPlatformAdmin = Boolean(user?.isPlatformAdmin);
  const isAdmin = Boolean(isOwner || isPlatformAdmin);

  const load = useCallback(async () => {
    if (!tenantId) {
      setSettings(DEFAULT_SETTINGS);
      setModules([]);
      setModulesKnown(false);
      setRoles([]);
      setPermissions(new Set());
      setIsManager(false);
      // There is nothing to wait for, so nothing may be left waiting. Without
      // this, losing the active organization strands `loading` at true.
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Independent calls to services, issued together.
      const [settingsResult, modulesResult, rolesResult, myPermsResult, hrProfileResult] = await Promise.allSettled([
        readLocalization(tenantId),
        platformApi.tenantModules(tenantId),
        iamApi.listRoles(),
        iamApi.myPermissions(),
        hrApi.getMyProfile(),
      ]);

      if (settingsResult.status === 'fulfilled' && settingsResult.value) {
        setSettings({ ...DEFAULT_SETTINGS, ...settingsResult.value });
        cacheLocalization(tenantId, settingsResult.value);
      }
      // Known even when the read failed: formatting with the defaults beats
      // holding every screen back for an outage.
      setLocalizationKnown(true);
      if (modulesResult.status === 'fulfilled') {
        setModules(modulesResult.value ?? []);
        setModulesKnown(Array.isArray(modulesResult.value) && modulesResult.value.length > 0);
      }
      if (rolesResult.status === 'fulfilled') setRoles(rolesResult.value ?? []);

      // The caller's own permissions are the UNION of every role their
      // membership carries — the same rule the server applies, so `can()`
      // agrees with what the server will actually allow. Owners hold
      // everything inside their organization.
      const roleList = rolesResult.status === 'fulfilled' ? rolesResult.value ?? [] : [];
      const myRoleIds = roleIdKey ? roleIdKey.split(',') : [];
      const myRoles = roleList.filter((role) => myRoleIds.includes(role.id));
      const myPermsData = myPermsResult.status === 'fulfilled' ? (myPermsResult.value?.data ?? myPermsResult.value) : null;

      const hrProfile = hrProfileResult?.status === 'fulfilled' ? (hrProfileResult.value?.data ?? hrProfileResult.value) : null;

      const myRoleKey = myPermsData?.roleKey || myRoles[0]?.key || '';
      const isEmployeeRole = myRoleKey === 'EMPLOYEE' || myRoles.some((role) => role.key === 'EMPLOYEE');
      const isManagerRole = Boolean(
        myRoleKey === 'TEAM_MANAGER' ||
        myRoleKey === 'HR_MANAGER' ||
        myRoleKey === 'HR_USER' ||
        myRoles.some((role) => role.key === 'TEAM_MANAGER' || role.key === 'HR_MANAGER' || role.key === 'HR_USER' || role.name?.toLowerCase().includes('manager'))
      );

      setIsManager(isManagerRole || (Boolean(hrProfile?.isManager) && !isEmployeeRole));

      // One role carrying "all permissions in this organization" wins outright
      if (isOwner || isPlatformAdmin || myRoles.some((role) => role.grantsAllTenantPermissions)) {
        setPermissions(new Set(['*']));
      } else {
        const rawPerms = myPermsData?.permissions;
        const apiPerms = Array.isArray(rawPerms)
          ? rawPerms
          : (rawPerms instanceof Set
            ? Array.from(rawPerms)
            : (rawPerms && typeof rawPerms === 'object'
              ? Object.keys(rawPerms)
              : []));

        const perms = new Set([
          ...apiPerms,
          ...myRoles.flatMap((role) => (role.permissions ?? []).map((permission) => permission.code || permission)),
        ]);

        // CRITICAL RULE:
        // A normal employee (EMPLOYEE role) strictly sees ONLY ESS (never MSS / hr.team.read).
        // Only managers (TEAM_MANAGER / HR_MANAGER) or HR admins get hr.team.read.
        if (!isEmployeeRole && (isManagerRole || hrProfile?.isManager || perms.has('hr.read'))) {
          perms.add('hr.team.read');
        } else if (isManagerRole) {
          perms.add('hr.team.read');
        }

        setPermissions(perms);
      }
    } catch (err) {
      console.error('TenantContext load error:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, roleIdKey, isOwner, isPlatformAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * UI-only permission check. Returns true if ANY of the codes is held.
   * The server re-checks every action regardless.
   *
   * IMPORTANT: this answers false while the permission set is still loading,
   * because an empty set holds nothing. "No" and "not yet" are therefore the
   * same answer here, and a screen that cannot tell them apart will confidently
   * tell an organization owner they have no access to the purchase module for
   * as long as the roles call takes. Screens should consult `loading` first —
   * or use `cannot()` below, which is the same question asked in the way that
   * makes the distinction impossible to miss.
   */
  const can = useCallback(
    (...codes) => {
      if (permissions.has('*')) return true;
      return codes.some((code) => permissions.has(code));
    },
    [permissions]
  );

  /**
   * True only when the permission set has LOADED and does not include any of
   * `codes` — i.e. a settled denial rather than an unanswered question.
   *
   * This is the one to reach for when the UI says something about the user
   * ("your role does not include..."), as opposed to merely hiding a button.
   */
  const cannot = useCallback(
    (...codes) => !loading && !can(...codes),
    [loading, can]
  );

  const isModuleEnabled = useCallback(
    (key) => modules.some((module) => module.key === key && module.enabled),
    [modules]
  );

  const value = useMemo(
    () => ({
      tenantId,
      organization: activeOrganization,
      settings,
      localizationKnown,
      currency: settings.currency ?? 'INR',
      modules,
      modulesKnown,
      roles,
      permissions,
      isManager,
      isOwner,
      isPlatformAdmin,
      isAdmin,
      loading,
      can,
      cannot,
      isModuleEnabled,
      reload: load,
      setSettings,
    }),
    [
      tenantId,
      activeOrganization,
      settings,
      localizationKnown,
      modules,
      modulesKnown,
      roles,
      permissions,
      isManager,
      isOwner,
      isPlatformAdmin,
      isAdmin,
      loading,
      can,
      cannot,
      isModuleEnabled,
      load,
    ]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used inside a TenantProvider');
  return context;
}
